// ---------------------------------------------- modules import
import M from "materialize-css";
import moment from "moment";
import React, { FunctionComponent, useEffect } from "react";
import ReactHtmlParser from "react-html-parser";
import { Link, useParams } from "react-router-dom";

import { ThemeContext } from "../../contexts/themeContext";
import { useAuth, usePinnedPostForDetails } from "./hooks";
import {
  IEmbedData,
  IHeaderData,
  IParagraphData,
  ISimpleImageData
} from "../../store/firebase/common";
import PermissionHelper from "../../store/helpers/permissionHelper";
import TagHelper from "../../store/helpers/tagHelper";

// ---------------------------------------------- the component
const Details: FunctionComponent = () => {
  // ---------------------------------------------- router state
  const { pinned_post_id } = useParams();

  // ---------------------------------------------- local state
  const { auth, profile, isEmptied } = useAuth();
  const { post, onUpdateReaders, onUnpin } = usePinnedPostForDetails(
    profile,
    pinned_post_id
  );

  const {
    id,
    title,
    data,
    category,
    author,
    createdAt,
    pinnedAt,
    indexOrigin,
    readers
  } = post;

  // ---------------------------------------------- handlers
  const handleUnpin = () => onUnpin(auth, profile, `/`);

  // ---------------------------------------------- effects
  useEffect(() => {
    const materialBoxes = document.querySelectorAll(".materialboxed");

    M.Materialbox.init(materialBoxes);
  }, [data]);

  useEffect(() => {
    const modals = document.querySelectorAll(".modal");

    M.Modal.init(modals);
  }, []);

  useEffect(() => {
    if (!profile.isEmpty && !profile.isSuper && readers.length) {
      const { uid } = auth;
      const { name, email } = profile;

      const found = readers.find(reader => reader.email === email);

      if (!found)
        onUpdateReaders(profile, [...readers, { id: uid, name, email }]);
    }
  }, [auth, profile, isEmptied, readers, onUpdateReaders]);

  // ---------------------------------------------- content
  return (
    <ThemeContext.Consumer>
      {({ theme }) => (
        <div className="container row">
          <div className="card col s12 m10 offset-m1">
            <div className="card-content" style={{ padding: "14px" }}>
              <span style={{ fontSize: "0.75rem" }}> pinned posts</span>

              <span
                className={`new badge ${
                  category === "human resource"
                    ? "indigo accent-2"
                    : category === "operational"
                    ? "green accent-3"
                    : category === "administration"
                    ? "orange accent-2"
                    : category === "report"
                    ? "red accent-2"
                    : "purple accent-2"
                }`}
                data-badge-caption={category}
              />

              <span
                className="card-title center-align"
                style={{ marginTop: "12px" }}
              >
                {title.toUpperCase()}
              </span>

              <div style={{ marginBottom: "8px" }}>
                <p style={{ fontSize: "0.75rem" }}>
                  {moment(createdAt.toDate()).calendar()}
                </p>

                <p className="grey-text" style={{ fontSize: "0.65rem" }}>
                  posted by {author.name} | {author.email}
                </p>
              </div>

              {data.blocks.map((block, index) =>
                block.type === "embed" ? (
                  <div
                    key={index}
                    className="video-container"
                    style={{ marginBottom: "8px" }}
                  >
                    <iframe
                      title={(block.data as IEmbedData).caption}
                      width={(block.data as IEmbedData).width}
                      height={(block.data as IEmbedData).height}
                      src={(block.data as IEmbedData).embed}
                      frameBorder="0"
                      allowFullScreen
                    ></iframe>
                  </div>
                ) : block.type === "header" ? (
                  (block.data as IHeaderData).level === 1 ? (
                    <h1 key={index}>{(block.data as IHeaderData).text}</h1>
                  ) : (block.data as IHeaderData).level === 2 ? (
                    <h2 key={index}>{(block.data as IHeaderData).text}</h2>
                  ) : (block.data as IHeaderData).level === 3 ? (
                    <h3 key={index}>{(block.data as IHeaderData).text}</h3>
                  ) : (block.data as IHeaderData).level === 4 ? (
                    <h4 key={index}>{(block.data as IHeaderData).text}</h4>
                  ) : (block.data as IHeaderData).level === 5 ? (
                    <h5 key={index}>{(block.data as IHeaderData).text}</h5>
                  ) : (
                    <h6 key={index}>{(block.data as IHeaderData).text}</h6>
                  )
                ) : block.type === "paragraph" ? (
                  <p key={index} style={{ margin: "10px 0" }}>
                    {ReactHtmlParser((block.data as IParagraphData).text)}
                  </p>
                ) : block.type === "simpleImage" ? (
                  <img
                    key={index}
                    className="materialboxed responsive-img"
                    src={(block.data as ISimpleImageData).url}
                    alt={(block.data as ISimpleImageData).caption}
                    data-caption={
                      (block.data as ISimpleImageData).caption
                        ? (block.data as ISimpleImageData).caption
                        : ""
                    }
                  />
                ) : null
              )}

              <p
                className="right-align"
                style={{ fontSize: "0.75rem", margin: "3px 0px" }}
              >{`Pinned on ${moment(pinnedAt.toDate()).calendar()}`}</p>

              <div className="divider" />

              <div className="right-align">
                {TagHelper.toTags(post.tags).map(tag =>
                  tag.tagged ? (
                    <span
                      key={tag.index}
                      className="grey-text text-darken-1"
                      style={{ fontSize: "0.65rem" }}
                    >{`\u00A0\u00A0\u00A0#${tag.shortName}`}</span>
                  ) : null
                )}
              </div>

              <p className="right-align" style={{ fontSize: "0.75rem" }}>
                {readers.length} reads, click{" "}
                <Link to={`/dashboard/reader-list/${id}`}>here</Link> to see
                details.
              </p>
            </div>

            <div className="card-action row" style={{ marginBottom: "0" }}>
              <div className="col s4">
                <Link
                  to={`/dashboard`}
                  className="btn-floating btn-small waves-effect waves-light"
                  style={{ backgroundColor: theme.ui }}
                >
                  <i className="material-icons left">arrow_back</i>
                </Link>
              </div>

              {profile &&
              PermissionHelper.getModify(profile.permissions, indexOrigin) ? (
                <div className="col s8 right-align">
                  <button
                    type="button"
                    className="btn-floating btn-small waves-effect waves-light modal-trigger"
                    data-target="alert"
                    style={{ backgroundColor: theme.ui, marginLeft: "5px" }}
                  >
                    <i className="tiny material-icons">bookmark_border</i>
                  </button>

                  <div className="modal" id="alert">
                    <div className="modal-content center">
                      <h5>Confirm Unpin Post</h5>

                      <p>
                        {`Are you sure you want to return this post to ${PermissionHelper.getName(
                          indexOrigin
                        )}?`}
                      </p>
                    </div>

                    <div className="modal-footer">
                      <div className="container">
                        <button
                          type="button"
                          className="btn modal-close"
                          style={{ backgroundColor: theme.ui }}
                          onClick={handleUnpin}
                        >
                          Yes, I'm sure
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </ThemeContext.Consumer>
  );
};

export default Details;
